import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { ScreeningStatusResponse } from '../api/risk-assessment-wizard';

interface ScreeningProgressModalProps {
  open: boolean;
  screeningStatus?: ScreeningStatusResponse | null;
  isInitialScreening: boolean;
  onBackToDashboard: () => void;
  onComplete: () => void;
}

/**
 * Progress bar component showing step completion.
 */
function ProgressBar({ currentStep, totalSteps }: { currentStep: number; totalSteps: number }) {
  const progress = (currentStep / totalSteps) * 100;
  return (
    <div className="w-full">
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
        <div
          className="h-full bg-[#131313] transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="mt-1 text-center text-xs text-gray-500">
        Step {currentStep} of {totalSteps}
      </p>
    </div>
  );
}

/**
 * Check item showing completion status.
 */
function CheckItem({
  label,
  completed,
  inProgress,
}: {
  label: string;
  completed: boolean;
  inProgress: boolean;
}) {
  return (
    <div className="flex items-center gap-3 py-2">
      {completed ? (
        <CheckCircle2 className="h-5 w-5 text-green-500" />
      ) : inProgress ? (
        <Loader2 className="h-5 w-5 animate-spin text-[#131313]" />
      ) : (
        <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
      )}
      <span
        className={completed ? 'text-green-600' : inProgress ? 'text-[#051923]' : 'text-gray-400'}
      >
        {label}
      </span>
    </div>
  );
}

/**
 * Screening Progress Modal - shows while initial screening checks are running.
 * This is separate from the existing screening-modal.tsx which is used for report generation.
 */
export function ScreeningProgressModal({
  open,
  screeningStatus,
  isInitialScreening,
  onBackToDashboard,
  onComplete,
}: ScreeningProgressModalProps) {
  const status = screeningStatus?.status;

  // While the screening mutation is pending (isInitialScreening) and we don't have fresh status data,
  // show the initial state (step 1 in progress)
  const isWaitingForFreshData = isInitialScreening && !status;

  // Status progression: FAST_CHECKS_DONE -> PROCESSING -> GENERATING_REPORT -> COMPLETED
  const fastChecksDone =
    !isWaitingForFreshData &&
    (status === 'FAST_CHECKS_DONE' ||
      status === 'PROCESSING' ||
      status === 'GENERATING_REPORT' ||
      status === 'COMPLETED');
  const adverseMediaDone =
    !isWaitingForFreshData && (status === 'GENERATING_REPORT' || status === 'COMPLETED');
  const isProcessingAdverseMedia = !isWaitingForFreshData && status === 'PROCESSING';
  const isGeneratingReport = !isWaitingForFreshData && status === 'GENERATING_REPORT';
  const completed = !isWaitingForFreshData && status === 'COMPLETED';
  const failed = !isWaitingForFreshData && status === 'FAILED';

  // Calculate current step for progress bar
  let currentStep = 1;
  if (fastChecksDone) currentStep = 2;
  if (adverseMediaDone) currentStep = 3;

  return (
    <Dialog open={open} onOpenChange={() => undefined}>
      <DialogContent className="sm:max-w-[400px]" hideCloseButton>
        <DialogHeader className="text-center sm:text-center">
          <DialogTitle className="text-[20px] font-semibold text-[#051923]">
            {completed
              ? 'Screening Complete'
              : failed
                ? 'Screening Failed'
                : 'Running Screening Checks'}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {/* Progress bar */}
          {!failed && <ProgressBar currentStep={currentStep} totalSteps={3} />}

          {/* Check items */}
          <div className="p-4">
            <CheckItem
              label="Running selected checks"
              completed={fastChecksDone}
              inProgress={isInitialScreening && !fastChecksDone}
            />
            <CheckItem
              label="In-depth analysis"
              completed={adverseMediaDone}
              inProgress={isProcessingAdverseMedia}
            />
            <CheckItem
              label="Generating report"
              completed={completed}
              inProgress={isGeneratingReport}
            />
          </div>

          {/* Error message */}
          {failed && screeningStatus?.adverseMedia.error && (
            <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
              {screeningStatus.adverseMedia.error}
            </div>
          )}

          {/* Info text */}
          {!completed && !failed && (
            <p className="text-center text-sm text-gray-500">
              If you navigate away, you will be notified when the report is ready.
            </p>
          )}
        </div>

        <div className="mt-4">
          {completed ? (
            <Button
              onClick={onComplete}
              className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[18px] font-medium leading-none tracking-normal text-white hover:bg-[#131313]/90"
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={onBackToDashboard}
              className="h-[45px] w-full rounded-[5px] border-[1.5px] border-[#131313] bg-white text-[18px] font-medium leading-none tracking-normal text-[#131313] hover:bg-gray-50"
            >
              Back to Dashboard
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
